<template>
  <Teleport to="body">
    <!--  Клик по пункту всплывает сюда и эмитит close, грид на него ставит visible = false (closeContextMenu).
      Двухуровневых меню нет, держать открытым после клика не нужно -->
    <!--  v-show, а не v-if: иначе меню размонтируется раньше, чем выполнится пункт, и событие contextMenuItem:done с результатом потеряется -->
    <div
      v-show="visible"
      ref="menu"
      class="datagrid-context-menu"
      :style="menuStyle"
      @click.stop="$emit('close')"
    >
      <!-- moveUp — канал для меню: «подними меня на N px», см. метод -->
      <slot v-if="row !== null" :row="row" :moveUp="moveUp" />
    </div>

    <div
      v-show="visible"
      class="datagrid-context-menu-overlay"
      @click="$emit('close')"
      @contextmenu.prevent="$emit('close')"
    />
  </Teleport>
</template>

<script lang="ts">

type Position = {
  top: number;
  left: number;
};

/** Отступ меню от краёв окна, когда оно не помещается, px */
const VIEWPORT_GAP = 10;

export default defineComponent({
  name: 'VDataGridContextMenuPopupV2',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** Где открыть меню; null — у курсора */
    position: {
      type: Object as PropType<Position | null>,
      default: null,
    },
    row: {
      type: Object as PropType<Record<string, unknown>>,
      default: null,
    },
  },

  emits: ['close'],

  data() {
    return {
      /** Где стоит меню: position или курсор, поднятые через moveUp и сдвинутые внутрь окна */
      adjustedPosition: { top: 0, left: 0 } as Position,
    };
  },

  computed: {
    menuStyle(): Record<string, string> {
      return {
        top: `${this.adjustedPosition.top}px`,
        left: `${this.adjustedPosition.left}px`,
      };
    },
  },

  watch: {
    /** Открыли — ставим меню в position или у курсора, после рендера (размер меню уже известен) сдвигаем внутрь окна */
    visible(val: boolean) {
      if (val === true) {
        if (this.position !== null) {
          this.adjustedPosition = { ...this.position };
        }
        else {
          const cursor = this.$core.utils.dom.getCursorCoords();
          this.adjustedPosition = { top: cursor.y, left: cursor.x };
        }

        this.$nextTick(() => this.adjustPosition());
      }
    },
  },

  methods: {
    /** Меню просит поднять его на offset px (высота пунктов над якорем) */
    moveUp(offset: number): void {
      this.adjustedPosition.top -= offset;
      this.adjustPosition();
    },

    adjustPosition(): void {
      const menu = this.$refs.menu as HTMLElement | undefined;
      if (this.$core.utils.isNullOrUndefined(menu) === true) return;

      const rect = menu.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      if (this.adjustedPosition.left + rect.width > viewport.width) {
        this.adjustedPosition.left = viewport.width - rect.width - VIEWPORT_GAP;
      }

      if (this.adjustedPosition.top + rect.height > viewport.height) {
        this.adjustedPosition.top = viewport.height - rect.height - VIEWPORT_GAP;
      }

      /** Поднятое через moveUp меню не должно уйти за верх окна */
      if (this.adjustedPosition.top < VIEWPORT_GAP) {
        this.adjustedPosition.top = VIEWPORT_GAP;
      }
    },
  },
});
</script>

<style lang="scss">
.datagrid-context-menu {
  position: fixed;
  z-index: 200;

  overflow: hidden;

  min-width: 180px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.datagrid-context-menu-overlay {
  position: fixed;
  z-index: 190;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
</style>
